// react
import { ReactNode, useContext } from 'react';

// types
import { IfrPlan, IfrPlanVersion } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

// context
import { ContextUser } from '../../../App.context';
import { ContextIfrPlans, ContextProject } from '../../pages/Project/Project.context';

// mui
import { Box, Grid, Link, Paper } from '@mui/material';

// icons
import {
	DriveFileRenameOutline as IconDriveFileRename,
	ModeEditOutlined as IconModeEdit,
} from '@mui/icons-material';

// ui
import { BtnMenu } from '../../ui/BtnMenu/BtnMenu';
import { ChipStatus } from '../../ui/ChipStatus/ChipStatus';
import { Currency } from '../../ui/Currency/Currency';
import { IconCircle } from '../../ui/IconCircle/IconCircle';
import { MenuItemProps } from '../../ui/Menu/Menu';
import { RowKeyValue } from '../../ui/RowKeyValue/RowKeyValue';
import { Time } from '../../ui/Time/Time';
import { TypographyCaption } from '../../ui/TypographyCaption/TypographyCaption';
import { UserAvatarModified } from '../../ui/UserAvatarModified/UserAvatarModified';

// dialogs
import { DialogEditIfrPlan } from '../../dialogs/DialogEditIfrPlan/DialogEditIfrPlan';

// theme
import { theme } from '../../../theme';

// props
export interface CardIfrPlanProps {
	plan: IfrPlan;
}

export const CardIfrPlan = ({ plan }: CardIfrPlanProps) => {
	// hooks
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDialog = searchParams.get('dialog');
	const paramDialogIfrPlan = searchParams.get('dialogIfrPlan');

	// context
	const { refetchIfrPlans } = useContext(ContextIfrPlans);
	const { project } = useContext(ContextProject);
	const { user } = useContext(ContextUser);

	// handlers
	const handleCloseDialog = () => {
		setSearchParams((params) => {
			params.delete('dialog');
			params.delete('dialogIfrPlan');
			return params;
		});
	};

	// vars
	let fy: ReactNode = '-';

	if (plan.dateFiscalYearStart && plan.dateFiscalYearEnd) {
		fy = (
			<>
				<Time date={plan.dateFiscalYearStart} />
				{' - '}
				<Time date={plan.dateFiscalYearEnd} />
			</>
		);
	}

	// menu items
	const btnMenuItems: MenuItemProps[] = [];
	if (user?.isWbg) {
		btnMenuItems.push(
			{
				children: t('editPlan'),
				component: RouterLink,
				icon: <IconModeEdit />,
				to: `/project/${project?.idWbg}/plan/${plan.id}`,
			},
			{
				children: t('renamePlan'),
				icon: <IconDriveFileRename />,
				onClick: () => {
					searchParams.set('dialog', 'editPlan');
					searchParams.set('dialogIfrPlan', String(plan.id));
					setSearchParams(searchParams);
				},
			}
		);
	}

	const hasFinalizedVersion = plan.versions.some((version) => version.status === 'FINALIZED');
	const hasDraftVersion = plan.versions.some((version) => version.status === 'DRAFT');
	const isRevision = hasFinalizedVersion && hasDraftVersion;

	const versionsSorted = plan.versions.sort((a: IfrPlanVersion, b: IfrPlanVersion) =>
		new Date(a.modified || a.created) < new Date(b.modified || b.created) ? 1 : -1
	);

	const displayStatus = isRevision
		? 'REVISION'
		: hasFinalizedVersion
			? 'FINALIZED'
			: versionsSorted?.[0]?.status;

	return (
		<>
			<Paper>
				<Box
					sx={(theme) => {
						return {
							position: 'relative',
							padding: theme.spacing(2),
						};
					}}>
					<Grid alignItems="center" container spacing={2}>
						<Grid item lg={2.5} xs={12}>
							<Grid alignItems="center" container spacing={2}>
								<Grid item>
									<IconCircle type="plan" />
								</Grid>
								<Grid flex={1} item>
									<TypographyCaption id={plan.id} />
									<Link
										component={RouterLink}
										to={`/project/${project?.idWbg}/plan/${plan.id}`}
										underline="none">
										{plan.name}
									</Link>
								</Grid>
							</Grid>
						</Grid>
						<Grid item lg={1.5} xs={12}>
							<RowKeyValue label={t('fiscalYear')} value={fy} />
						</Grid>
						<Grid item lg={1} xs={12}>
							<RowKeyValue label={t('currency')} value={<Currency currency={plan.currency} />} />
						</Grid>
						<Grid item lg={2} xs={12}>
							<RowKeyValue label={t('status')} value={<ChipStatus status={displayStatus} />} />
						</Grid>
						<Grid item lg={2} xs={12}>
							<RowKeyValue
								label={t('created')}
								value={<UserAvatarModified ifrPlan={plan} activityTypes={['IFR_PLAN_CREATED']} />}
							/>
						</Grid>
						<Grid item lg={2} xs={12}>
							<RowKeyValue
								label={t('modified')}
								value={
									<UserAvatarModified
										ifrPlan={plan}
										activityTypes={['IFR_PLAN_UPDATED', 'IFR_PLAN_CREATED']}
									/>
								}
							/>
						</Grid>
						{btnMenuItems.length > 0 && (
							<Grid item lg={true} xs={12}>
								<Grid container justifyContent={{ lg: 'flex-end' }} spacing={1}>
									<Grid item>
										<Grid
											item
											sx={{
												position: 'absolute',
												top: theme.spacing(2),
												right: theme.spacing(2),
												[theme.breakpoints.up('lg')]: {
													position: 'relative',
													top: 'auto',
													right: 'auto',
												},
											}}>
											<BtnMenu items={btnMenuItems} />
										</Grid>
									</Grid>
								</Grid>
							</Grid>
						)}
					</Grid>
				</Box>
			</Paper>
			<DialogEditIfrPlan
				isOpen={paramDialog === 'editPlan' && paramDialogIfrPlan === String(plan.id)}
				onClose={handleCloseDialog}
				onSuccess={() => {
					refetchIfrPlans();
				}}
			/>
		</>
	);
};
